import * as React from 'react'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogTitle,
    DialogTrigger,
    Field,
    MessageBar,
    MessageBarBody,
    Radio,
    RadioGroup,
} from '@fluentui/react-components'
import { useFormikContext } from 'formik'
import { FormikConfig } from 'formik/dist/types'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import HammockFormDialog, { HammockFormDialogProps } from '../../v2/components/Dialog/HammockFormDialog'
import FormikComboboxField from '../../components/Formik/FormikComboboxField'
import FileCombobox, { FileComboboxProps } from '../../components/Combobox/FileCombobox'
import FormContainer from '../../v2/components/Form/FormContainer'
import { ExtendedTimeOverviewDto } from '../../v2/pages/Time/BaseTimeOverview'
import { RateDto } from '../../config/fileConfig'
import { BaseTierDto } from '../../config/finance/financeConfig'
import { resolveRate } from '../../helpers/rateHelper'
import { formatShortNumericDate } from '../../helpers/dateHelper'

enum RateMode {
    Keep = 'KEEP',
    Recalculate = 'RECALCULATE',
}

type MoveTimesValues = {
    targetFileId?: string
    rateMode: RateMode
}

// Mirrors the backend's TimeMoveViolationDto; reason is one of billed | otherAdvice | currency.
type TimeMoveViolation = {
    timeId: string
    date: string
    description: string
    reason: string
}

type MoveTimesDialogProps = {
    approvalId: string
    selectedTimes: ExtendedTimeOverviewDto[]
    open: boolean
    setOpen: (open: boolean) => void
    onMoved: (movedIds: string[]) => void
}

function MoveTimesDialogBody(props: HammockFormDialogProps<MoveTimesValues> & { dialogProps: MoveTimesDialogProps }): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const { values, setFieldValue, isSubmitting, resetForm } = useFormikContext<MoveTimesValues>()

    const [violations, setViolations] = useState<TimeMoveViolation[]>()

    const handleSubmit = useCallback<FormikConfig<MoveTimesValues>['onSubmit']>(
        submitted => {
            setViolations(undefined)
            const items =
                submitted.rateMode === RateMode.Recalculate
                    ? axios.get<RateDto<BaseTierDto>>(`/file/${submitted.targetFileId}/rate`).then(response =>
                          props.dialogProps.selectedTimes.map(time => ({
                              timeId: time.id,
                              rate: resolveRate(response.data, time.user?.id, time.date),
                          })),
                      )
                    : Promise.resolve(props.dialogProps.selectedTimes.map(time => ({ timeId: time.id, rate: null })))

            return items
                .then(resolvedItems =>
                    axios.post(
                        `/finance/invoice-approval/${props.dialogProps.approvalId}/time/move`,
                        { targetFileId: submitted.targetFileId, items: resolvedItems },
                        { headers: { SuppressErrors: 'true' } },
                    ),
                )
                .then(() => {
                    props.dialogProps.onMoved(props.dialogProps.selectedTimes.map(time => time.id))
                    resetForm()
                })
                .catch(e => {
                    if (e?.response?.status === 409 && e.response.data?.message === 'timeMoveBlocked') {
                        setViolations(e.response.data.violations)
                        return true
                    }
                    throw e
                })
        },
        [props.dialogProps.selectedTimes, props.dialogProps.approvalId],
    )

    useEffect(() => {
        props.setOnSubmit?.(handleSubmit)
    }, [handleSubmit])

    const handleCancel = () => {
        setViolations(undefined)
        resetForm()
        props.onDismiss()
    }

    return (
        <DialogBody>
            <DialogTitle>{translate('invoiceapproval.times.move.title')}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>
                            {translate('invoiceapproval.times.move.warning', { count: props.dialogProps.selectedTimes.length })}
                        </MessageBarBody>
                    </MessageBar>
                    {violations?.length > 0 && (
                        <MessageBar intent={'error'} layout={'multiline'}>
                            <MessageBarBody>
                                {translate('invoiceapproval.times.move.error.blocked')}
                                <ul>
                                    {violations.map(violation => (
                                        <li key={violation.timeId}>
                                            {formatShortNumericDate(violation.date)} {violation.description} —{' '}
                                            {translate(`invoiceapproval.times.move.error.${violation.reason.toLowerCase()}`)}
                                        </li>
                                    ))}
                                </ul>
                            </MessageBarBody>
                        </MessageBar>
                    )}
                    <FormikComboboxField<MoveTimesValues, FileComboboxProps>
                        required
                        fieldName={'targetFileId'}
                        component={FileCombobox}
                        labelKey={'invoiceapproval.times.move.target'}
                    />
                    <Field label={translate('invoiceapproval.times.move.rate.label')}>
                        <RadioGroup value={values.rateMode} onChange={(_, data) => setFieldValue('rateMode', data.value)}>
                            <Radio value={RateMode.Recalculate} label={translate('invoiceapproval.times.move.rate.recalculate')} />
                            <Radio value={RateMode.Keep} label={translate('invoiceapproval.times.move.rate.keep')} />
                        </RadioGroup>
                    </Field>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button appearance="primary" type="submit" disabled={isSubmitting}>
                    {translate('invoiceapproval.times.move.confirm', { count: props.dialogProps.selectedTimes.length })}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary" onClick={handleCancel} disabled={isSubmitting}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}

export default function MoveTimesDialog(props: MoveTimesDialogProps): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <HammockFormDialog<MoveTimesValues>
            open={props.open}
            setOpen={props.setOpen}
            dialogFormProps={() => ({
                initialValues: { targetFileId: undefined, rateMode: RateMode.Recalculate },
                onSubmit: () => {},
                validationSchema: Yup.object().shape({
                    targetFileId: Yup.string().uuid().label(translate('invoiceapproval.times.move.target')).required(),
                }),
            })}
            dialogFormBody={bodyProps => <MoveTimesDialogBody {...bodyProps} dialogProps={props} />}
        />
    )
}
